import React, { useState, useEffect } from 'react';
import { TextInput, Textarea, Select, Checkbox, Button } from '@mantine/core';
import { useTracking } from '@mygpt/app';
import { useSearchParams } from 'react-router-dom';
interface FormErrors {
  companyName?: string;
  department?: string;
  name?: string;
  email?: string;
  phone?: string;
  employees?: string;
  inquiryType?: string;
  inquiryContent?: string;
  privacyPolicy?: string;
}

const ContactPage: React.FC = () => {
  const { trackCustomEvent } = useTracking();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    companyName: '',
    department: '',
    name: '',
    email: '',
    phone: '',
    employees: '',
    inquiryType: '',
    inquiryContent: '',
    privacyPolicy: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [isInquiryTypeLocked, setIsInquiryTypeLocked] = useState(false);

  // 检查URL参数，如果source=GBaseGTM则自动填充并锁定
  useEffect(() => {
    const source = searchParams.get('source');
    if (source === 'GBaseGTM') {
      setFormData((prev) => ({
        ...prev,
        inquiryType: 'GBaseGTM',
      }));
      setIsInquiryTypeLocked(true);
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const validate = () => {
    const newErrors: FormErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.companyName) newErrors.companyName = '企業名/組織名は必須です';
    if (!formData.department) newErrors.department = '所属部署・役職は必須です';
    if (!formData.name) newErrors.name = 'お名前は必須です';
    if (!formData.email) {
      newErrors.email = 'メールアドレスは必須です';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'メールアドレスの形式が正しくありません';
    }
    if (!formData.phone) newErrors.phone = '電話番号は必須です';
    if (!formData.inquiryType) newErrors.inquiryType = 'お問い合わせ種別は必須です';
    if (!formData.inquiryContent) newErrors.inquiryContent = 'お問い合わせ内容は必須です';
    if (!formData.privacyPolicy) newErrors.privacyPolicy = 'プライバシーポリシーへの同意は必須です';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      try {
        const response = await fetch('https://api.studiodesignapp.com/api/v2/projects/BXax798Xq7/form', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            data: [
              ['企業名/組織名', formData.companyName, 'TEXT'],
              ['所属部署・役職', formData.department, 'TEXT'],
              ['お名前', formData.name, 'TEXT'],
              ['メールアドレス', formData.email, 'EMAIL'],
              ['電話番号', formData.phone, 'TEL'],
              ['従業員数', formData.employees, 'TEXT'],
              ['お問い合わせ種別', formData.inquiryType, 'SELECT'],
              ['お問い合わせ内容', formData.inquiryContent, 'TEXTAREA'],
              ['プライバシーポリシーへの同意', formData.privacyPolicy ? 'on' : 'off', 'CHECKBOX'],
            ],
            form_name: 'お問い合わせ',
            is_preview: false,
          }),
        });

        if (response.ok) {
          setSubmitted(true);
          trackCustomEvent('contact_submit_success', {
            event_category: 'form_submit',
            event_label: 'contact_form',
          });
        } else {
          console.error('Failed to submit form');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    }
  };

  const handleSelectChange = (value: string | null) => {
    setFormData({
      ...formData,
      inquiryType: value || '',
    });
  };

  return (
    <div>
      <div className="max-w-full lg:max-w-[50vw] mx-auto p-4 lg:p-6 mt-[130px] text-left">
        {submitted ? (
          <div className="max-w-full lg:max-w-lg mx-auto p-4 lg:p-6 bg-gray-50 shadow-md rounded-lg text-center">
            <h2 className="text-xl lg:text-2xl font-bold mb-4">お問い合わせを受け付けました</h2>
            <p className="text-lg lg:text-xl mb-3">
              お問い合わせありがとうございます。内容確認後、担当者より通常1〜2営業日以内にご連絡いたします。
            </p>
            <p className="text-lg lg:text-xl mb-3">
              しばらく経っても弊社からメールが届かない場合は、入力頂いたメールアドレスが間違っているか、迷惑メールフォルダに振り分けられている可能性がございますので、再度お問い合わせをお願いいたします。
            </p>
            <p className="text-lg lg:text-xl mb-3">
              また、ドメイン指定をされている場合は、「@sparticle.com」からのメールが受信できるようあらかじめ設定をお願いいたします。
            </p>
            <p className="text-lg lg:text-xl mb-3">
              なお、お急ぎの場合は電話でのお問い合わせも受け付けております。03-3527-2828までご遠慮なくご相談ください。
            </p>
            <a
              href="/"
              className="inline-block mt-5 px-4 lg:px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
            >
              トップへ戻る
            </a>
          </div>
        ) : (
          <>
            <p className="text-lg lg:text-xl mb-3">GBaseにご興味をお持ちいただき誠にありがとうございます。</p>
            <p className="text-lg lg:text-xl mb-3">
              お問い合わせ、ご相談がございましたら、下記フォームよりお気軽にご連絡ください。
            </p>
            <p className="text-lg lg:text-xl mb-3">内容確認後、担当者より通常1〜2営業日以内にご連絡いたします。</p>
          </>
        )}
      </div>
      <div className="max-w-full lg:max-w-[50vw] mx-auto p-4 bg-white shadow-md rounded-lg mb-10">
        {!submitted && (
          <>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <TextInput
                label="企業名/組織名"
                placeholder="株式会社○○"
                required
                className="w-full text-base lg:text-lg leading-6"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                error={errors.companyName}
              />
              <TextInput
                label="所属部署・役職"
                placeholder="DX推進部 課長"
                required
                className="w-full text-base lg:text-lg leading-6"
                name="department"
                value={formData.department}
                onChange={handleChange}
                error={errors.department}
              />
              <TextInput
                label="お名前"
                placeholder="山田 太郎"
                required
                className="w-full text-base lg:text-lg leading-6"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
              />
              <TextInput
                label="メールアドレス"
                placeholder="xxx@gbase.ai"
                type="email"
                required
                className="w-full text-base lg:text-lg leading-6"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
              />
              <TextInput
                label="電話番号"
                placeholder="000-0000-0000"
                type="tel"
                required
                className="w-full text-base lg:text-lg leading-6"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={errors.phone}
              />
              <TextInput
                label="従業員数"
                placeholder="123名"
                className="w-full text-base lg:text-lg leading-6"
                required
                name="employees"
                value={formData.employees}
                onChange={handleChange}
              />
              <Select
                label="お問い合わせ種別"
                placeholder="選択してください"
                data={[
                  { value: 'お申し込み', label: 'お申し込み' },
                  { value: 'サービスについての問い合わせ', label: 'サービスについての問い合わせ' },
                  { value: 'GBaseGTM', label: 'GBaseGTM' },
                  { value: 'その他', label: 'その他' },
                ]}
                required
                className="w-full text-base lg:text-lg leading-6"
                name="inquiryType"
                value={formData.inquiryType}
                onChange={handleSelectChange}
                error={errors.inquiryType}
                disabled={isInquiryTypeLocked}
              />
              <Textarea
                label="お問い合わせ内容"
                placeholder="お問い合わせ内容を入力してください"
                required
                className="w-full text-base lg:text-lg leading-6"
                name="inquiryContent"
                value={formData.inquiryContent}
                onChange={handleChange}
                error={errors.inquiryContent}
              />
              <Checkbox
                label={
                  <span>
                    プライバシーポリシーに同意する{' '}
                    <a href="/privacy-policy" target="_blank" className="text-violet-500 underline">
                      プライバシーポリシー
                    </a>
                  </span>
                }
                required
                name="privacyPolicy"
                checked={formData.privacyPolicy}
                onChange={handleChange}
                error={errors.privacyPolicy}
              />
              <div className="flex justify-center mt-4">
                <Button type="submit" className="bg-violet-500 text-white hover:bg-violet-500/80">
                  この内容で送信する
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactPage;
